import React, {
  useState,
  useEffect,
} from 'react';
import { Amplify } from 'aws-amplify';
import { withAuthenticator } from '@aws-amplify/ui-react';
import '../../styles.css';
import './App.css';
import MarkdownIt from 'markdown-it';
import {
  BrowserRouter as Router,
  Route,
  Routes,
} from 'react-router-dom';
import awsconfig from '../../aws-exports';
import Header from '../../components/Header/Header';
import Settings from '../../components/Settings/Settings';
import ChatGPT4Settings from '../../components/Settings/ChatGPT4Settings';
import ActionButtons from '../../components/ActionButtons/ActionButtons';
import ConvertedContent from '../../components/ConvertedContent/ConvertedContent';
import ChatGPT4 from '../../components/ChatGPT4/ChatGPT4';
import { CONSTANTS } from '../../utils/constants';
import { AppProvider, useAppState, useAppDispatch } from '../../context/AppContext';
import { saveChatLog, ChatService } from '../../services/ChatService';
import { OpenAIGPT4Strategy } from '../../services/ChatStrategies';

// Configure Amplify
Amplify.configure(awsconfig);

const chatService = new ChatService(new OpenAIGPT4Strategy());

const App = () => {
  const username = 'mockUser123';
  const [markdownText, setMarkdownText] = useState('');
  const [htmlText, setHtmlText] = useState('');
  const [userProfiles, setUserProfiles] = useState([]);
  const [activeProfile, setActiveProfile] = useState(null);
  const [activeFont, setActiveFont] = useState(CONSTANTS.FONTS[0]);
  const [activeFontSize, setActiveFontSize] = useState('16px');
  const [activeLineSpacing, setActiveLineSpacing] = useState('1.2');
  const [activeColorScheme, setActiveColorScheme] = useState(CONSTANTS.COLOR_SCHEMES[0]);
  const [notificationsList, setNotificationsList] = useState([]);
  const [messageTimestamps, setMessageTimestamps] = useState([]);
  const [chatLogs, setChatLogs] = useState([]);
    
  const { totalTokens, tokensLastHour, messagesLastThreeHours } = useAppState();
  const dispatch = useAppDispatch();

  const updateTokenCounts = (newTokens, newMessages) => {
    dispatch({ type: 'SET_TOTAL_TOKENS', payload: totalTokens + newTokens });
    dispatch({ type: 'SET_TOKENS_LAST_HOUR', payload: tokensLastHour + newTokens });
    if (newMessages) {
      dispatch({ type: 'SET_MESSAGES_LAST_THREE_HOURS', payload: messagesLastThreeHours + newMessages });
      const newTimestamp = new Date();
      setMessageTimestamps((prevTimestamps) => [...prevTimestamps, newTimestamp]);
    }
  };

  const defaultModelSettings = {
    temperature: 0.85,
    maxTokens: 5500,
    prompt: '',
    role: 'Assistant',
    topP: 0.0,
    frequencyPenalty: 0.2,
    presencePenalty: 0.2,
    stopSequences: ['\n'],
    echo: true,
    modelVersion: 'gpt-4',
    stream: false,
    bestOf: null,
  };
  
  const initialModelSettings = JSON.parse(sessionStorage.getItem('modelSettings')) || defaultModelSettings;
  const [modelSettings, setModelSettings] = useState(initialModelSettings);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch({ type: 'SET_TOKENS_LAST_HOUR', payload: 0 });
    }, 3600000);
    return () => clearTimeout(timer);
  }, [tokensLastHour]);

  useEffect(() => {
    const now = new Date();
    const threeHoursAgo = new Date(now.getTime() - 3 * 3600000);
    const recentMessages = messageTimestamps.filter((timestamp) => timestamp > threeHoursAgo);
    dispatch({ type: 'SET_MESSAGES_LAST_THREE_HOURS', payload: recentMessages.length });

    // TODO: You'll also need to update tokensLastThreeHours based on the tokens of these messages,
    // which might require additional state or a more complex data structure for messageTimestamps.
  }, [messageTimestamps]);
  
  const mdParser = new MarkdownIt();

  const deleteProfile = () => {
    if (!activeProfile) {
      alert('No profile selected for deletion.');
      return;
    }
    const updatedProfiles = userProfiles.filter((profile) => profile.name !== activeProfile);
    setUserProfiles(updatedProfiles);
    localStorage.setItem('chatFormat_profiles', JSON.stringify(updatedProfiles));
    setActiveProfile(null);
  };

  const updateProfileSettings = (profileName) => {
    const selectedProfile = userProfiles.find((profile) => profile.name === profileName);
    if (selectedProfile) {
      setActiveFont(selectedProfile.font);
      setActiveFontSize(selectedProfile.fontSize);
      setActiveLineSpacing(selectedProfile.lineSpacing);
      setActiveColorScheme(selectedProfile.colorScheme);
      setActiveProfile(profileName);
    }
  };

  const persistProfile = () => {
    if (userProfiles.length >= CONSTANTS.MAX_PROFILES) {
      alert(`You can only have up to ${CONSTANTS.MAX_PROFILES} profiles.`);
      return;
    }
    const profileName = prompt('Provide a name for the new profile:', `Profile ${userProfiles.length + 1}`) || `Profile ${userProfiles.length + 1}`;
    const newProfile = {
      name: profileName,
      font: activeFont,
      fontSize: activeFontSize,
      lineSpacing: activeLineSpacing,
      colorScheme: activeColorScheme,
    };
    const updatedProfiles = [...userProfiles, newProfile];
    setUserProfiles(updatedProfiles);
    setActiveProfile(profileName);
    localStorage.setItem('chatFormat_profiles', JSON.stringify(updatedProfiles));
  };

  const convertMarkdownToHtml = () => {
    const generatedHtml = mdParser.render(markdownText);
    setHtmlText(generatedHtml);
  };

  const handleCopy = () => {
    const range = document.createRange();
    const selection = window.getSelection();
    range.selectNode(document.querySelector('.converted-content'));
    selection.removeAllRanges();
    selection.addRange(range);
    document.execCommand('copy');
    selection.removeAllRanges();
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setMarkdownText(text);
    } catch (err) {
      console.error('Failed to read clipboard contents: ', err);
    }
  };

  const handleClear = () => {
    setMarkdownText('');
    setHtmlText('');
  };

  const handlePrint = () => {
    window.print();
  };

  const getAllChatLogs = async () => {
    try {
      const result = await chatService.getAllChatLogs(username);
      if (result.success) {
        setChatLogs(result.chatLogs);
      } else {
        console.error(result.message);
      }
    } catch (error) {
      console.error('Error fetching all chat logs:', error);
    }
  };

  useEffect(() => {
    getAllChatLogs();
  }, [username, activeColorScheme, userProfiles, markdownText]);
  
  return (
    <Router>
      <div className={`App ${CONSTANTS.COLOR_SCHEME_CLASSES[activeColorScheme]}`}>
        <Header notifications={notificationsList} setNotifications={setNotificationsList} />
        <Routes>
          <Route
            path="/"
            element={(
              <div>
                <div>
                  <p>
                    Total Tkn:
                    {totalTokens}
                    | Last Hr:
                    {tokensLastHour}
                    | Msg Ct Last 3 Hrs:
                    {messagesLastThreeHours}
                  </p>
                  <div className="chat-log">
                    {chatLogs.map((log) => (
                      <div key={log.id} className="chat-log-entry">
                        <span className="timestamp">{log.timestamp}</span>
                        <span className="message">{log.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <ChatGPT4
                  setMarkdownText={setMarkdownText}
                  markdownText={markdownText}
                  updateTokenCounts={updateTokenCounts}
                  modelSettings={modelSettings}
                  onMessageReceived={(newMessage) => {
                    const newLog = {
                      message: newMessage,
                      timestamp: new Date().toISOString(),
                    };
                    setChatLogs((prevLogs) => [...prevLogs, newLog]);
                    saveChatLog(newLog, process.env.REACT_APP_BACKEND_URL);
                  }}
                />
                <Settings
                  profiles={userProfiles}
                  currentProfile={activeProfile}
                  deleteProfile={deleteProfile}
                  font={activeFont}
                  fontSize={activeFontSize}
                  lineSpacing={activeLineSpacing}
                  colorScheme={activeColorScheme}
                  handleProfileChange={updateProfileSettings}
                  saveProfile={persistProfile}
                  setFont={setActiveFont}
                  setFontSize={setActiveFontSize}
                  setLineSpacing={setActiveLineSpacing}
                  setColorScheme={setActiveColorScheme}
                />
                <textarea
                  className="markdown-input"
                  value={markdownText}
                  onChange={(e) => setMarkdownText(e.target.value)}
                  aria-label="Input Markdown here"
                />
                <ActionButtons
                  handleConversion={convertMarkdownToHtml}
                  handleCopy={handleCopy}
                  handlePaste={handlePaste}
                  handleClear={handleClear}
                  handlePrint={handlePrint}
                />
                <ConvertedContent
                  font={activeFont}
                  fontSize={activeFontSize}
                  lineSpacing={activeLineSpacing}
                  convertedHTML={htmlText}
                />
              </div>
            )}
          />
          <Route
            path="/settings"
            element={(
              <ChatGPT4Settings
                modelSettings={modelSettings}
                setModelSettings={setModelSettings}
              />
            )}
          />
        </Routes>
      </div>
    </Router>
  );
};
  
export default withAuthenticator(() => (
  <AppProvider>
    <App />
  </AppProvider>
));
